import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  ArrowLeft, BarChart3, ArrowDownCircle, ArrowUpCircle, 
  Filter, Calendar, Clock, CheckCircle, XCircle, Search,
  TrendingUp, TrendingDown, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const History = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("trades");
  const [trades, setTrades] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [assetFilter, setAssetFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [tradesRes, withdrawalsRes, depositsRes] = await Promise.all([
        axios.get(`${API}/trades`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/withdrawals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/deposits`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTrades(tradesRes.data);
      setWithdrawals(withdrawalsRes.data);
      setDeposits(depositsRes.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Moscow"
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Moscow"
    });
  };

  // Filter trades
  const filteredTrades = trades.filter(trade => {
    if (statusFilter !== "all" && trade.status !== statusFilter) return false;
    if (assetFilter && !trade.asset_name?.toLowerCase().includes(assetFilter.toLowerCase())) return false;
    if (dateFrom) {
      const tradeDate = new Date(trade.close_date || trade.created_at);
      if (tradeDate < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      const tradeDate = new Date(trade.close_date || trade.created_at);
      if (tradeDate > new Date(dateTo + "T23:59:59")) return false;
    }
    return true;
  });

  // Filter withdrawals
  const filteredWithdrawals = withdrawals.filter(w => {
    if (statusFilter !== "all" && w.status !== statusFilter) return false;
    if (dateFrom && new Date(w.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(w.created_at) > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  // Filter deposits
  const filteredDeposits = deposits.filter(d => {
    if (dateFrom && new Date(d.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(d.created_at) > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  // Get unique assets for filter dropdown
  const uniqueAssets = [...new Set(trades.map(t => t.asset_name).filter(Boolean))];

  const clearFilters = () => {
    setStatusFilter("all");
    setAssetFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const getStatusBadge = (status, type = "trade") => {
    if (type === "trade") {
      if (status === "active") {
        return (
          <span className="inline-flex items-center gap-1 bg-blue-500/15 text-blue-500 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <Clock size={12} />
            В работе
          </span>
        );
      } else if (status === "pending") {
        return (
          <span className="inline-flex items-center gap-1 bg-white/15 text-[#EDEDED] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <Clock size={12} />
            В ожидании
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center gap-1 bg-[#A1A1AA]/12 text-[#A1A1AA] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <CheckCircle size={12} className="text-[#34D399]" />
            Выполнено
          </span>
        );
      }
    } else {
      // withdrawal status
      if (status === "pending") {
        return (
          <span className="inline-flex items-center gap-1 bg-white/15 text-[#EDEDED] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <Clock size={12} />
            В обработке
          </span>
        );
      } else if (status === "completed") {
        return (
          <span className="inline-flex items-center gap-1 bg-[#34D399]/15 text-[#34D399] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <CheckCircle size={12} />
            Выполнено
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center gap-1 bg-[#F87171]/15 text-[#F87171] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
            <XCircle size={12} />
            Отклонено
          </span>
        );
      }
    }
  };

  // Calculate totals
  const totalProfit = filteredTrades
    .filter(t => t.status === "closed")
    .reduce((sum, t) => sum + (t.current_income || t.income || 0), 0);
  
  const totalInvestment = filteredTrades.reduce((sum, t) => sum + (t.investment || 0), 0);
  
  const totalWithdrawals = filteredWithdrawals
    .filter(w => w.status === "completed")
    .reduce((sum, w) => sum + w.amount, 0);

  const totalDepositsAmount = filteredDeposits.reduce((sum, d) => sum + d.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bybit-grid-subtle flex items-center justify-center">
        <div className="text-[#EDEDED]">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-[#A1A1AA] hover:text-white transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="font-bold text-xl text-white">История операций</h1>
            </div>
            <Link
              to="/dashboard"
              className="text-[#EDEDED] hover:text-[#EDEDED] transition-colors font-medium"
            >
              Дашборд
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#34D399]/20 flex items-center justify-center">
                <TrendingUp size={20} className="text-[#34D399]" />
              </div>
              <div>
                <div className="text-[#A1A1AA] text-sm">Общий доход</div>
                <div className="font-mono text-xl font-bold text-white">
                  {totalProfit >= 0 ? '+' : ''}${totalProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <BarChart3 size={20} className="text-[#EDEDED]" />
              </div>
              <div>
                <div className="text-[#A1A1AA] text-sm">Инвестировано</div>
                <div className="font-mono text-xl font-bold text-white">
                  ${totalInvestment.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#34D399]/20 flex items-center justify-center">
                <ArrowDownCircle size={20} className="text-[#34D399]" />
              </div>
              <div>
                <div className="text-[#A1A1AA] text-sm">Пополнено</div>
                <div className="font-mono text-xl font-bold text-white">
                  ${totalDepositsAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <ArrowUpCircle size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-[#A1A1AA] text-sm">Выведено</div>
                <div className="font-mono text-xl font-bold text-white">
                  ${totalWithdrawals.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#1F1F1F] overflow-x-auto">
          <button
            onClick={() => setActiveTab("trades")}
            className={`pb-3 px-4 font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
              activeTab === "trades" 
                ? "text-[#FF8A1E] border-b-2 border-[#FF8A1E]" 
                : "text-[#A1A1AA] hover:text-white"
            }`}
          >
            <BarChart3 size={18} />
            Сделки ({filteredTrades.length})
          </button>
          <button
            onClick={() => setActiveTab("deposits")}
            className={`pb-3 px-4 font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
              activeTab === "deposits" 
                ? "text-[#FF8A1E] border-b-2 border-[#FF8A1E]" 
                : "text-[#A1A1AA] hover:text-white"
            }`}
          >
            <ArrowDownCircle size={18} />
            Пополнения ({filteredDeposits.length})
          </button>
          <button
            onClick={() => setActiveTab("withdrawals")}
            className={`pb-3 px-4 font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
              activeTab === "withdrawals" 
                ? "text-[#FF8A1E] border-b-2 border-[#FF8A1E]" 
                : "text-[#A1A1AA] hover:text-white"
            }`}
          >
            <ArrowUpCircle size={18} />
            Выводы ({filteredWithdrawals.length})
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-4"
          >
            <Filter size={18} />
            <span>Фильтры</span>
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          
          {showFilters && (
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Статус</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white focus:border-white focus:outline-none"
                  >
                    <option value="all">Все</option>
                    {activeTab === "trades" ? (
                      <>
                        <option value="active">В работе</option>
                        <option value="closed">Выполнено</option>
                      </>
                    ) : (
                      <>
                        <option value="pending">В обработке</option>
                        <option value="completed">Выполнено</option>
                        <option value="rejected">Отклонено</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Asset Filter (only for trades) */}
                {activeTab === "trades" && (
                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">Актив</label>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                      <input
                        type="text"
                        value={assetFilter}
                        onChange={(e) => setAssetFilter(e.target.value)}
                        placeholder="Поиск..."
                        className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-9 pr-3 py-2 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* Date From */}
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Дата от</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Дата до</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-3 py-2 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                  />
                </div>
              </div>
              
              <button
                onClick={clearFilters}
                className="mt-4 text-[#A1A1AA] hover:text-white text-sm transition-colors"
              >
                Сбросить фильтры
              </button>
            </div>
          )}
        </div>

        {/* Trades Table */}
        {activeTab === "trades" && (
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-[#1F1F1F]">
              {filteredTrades.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">Нет сделок</div>
              ) : (
                filteredTrades.map((trade) => {
                  const income = trade.current_income || trade.income || 0;
                  const pnl = trade.current_pnl_percent || trade.pnl_percent || 0;
                  const isPositive = pnl >= 0;
                  const pnlColor = isPositive ? "text-[#34D399]" : "text-[#F87171]";
                  return (
                    <div key={trade.id} className="p-4" data-testid={`history-trade-card-${trade.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white font-semibold">{trade.asset_name}</span>
                        {getStatusBadge(trade.status, "trade")}
                      </div>
                      <div className="grid grid-cols-2 gap-y-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-0.5">Инвестиция</div>
                          <div className="font-mono text-sm text-white">${trade.investment?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-0.5">Доход</div>
                          <div className={`font-mono text-sm ${pnlColor}`}>{isPositive ? '+' : ''}${income.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-0.5">PnL</div>
                          <div className={`font-mono text-sm font-semibold ${pnlColor}`}>{isPositive ? '+' : ''}{pnl.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-0.5">Дата</div>
                          <div className="font-mono text-xs text-[#A1A1AA]">{formatShortDate(trade.close_date || trade.created_at)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1F1F1F]">
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Актив</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Инвестиция</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Доход</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">PnL</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-[#A1A1AA]">
                        Нет сделок
                      </td>
                    </tr>
                  ) : (
                    filteredTrades.map((trade) => {
                      const income = trade.current_income || trade.income || 0;
                      const pnl = trade.current_pnl_percent || trade.pnl_percent || 0;
                      const isPositive = pnl >= 0;
                      
                      return (
                        <tr key={trade.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50 transition-colors">
                          <td className="py-4 px-4 text-[#A1A1AA] text-sm font-mono">
                            {formatShortDate(trade.close_date || trade.created_at)}
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white font-medium">{trade.asset_name}</span>
                          </td>
                          <td className="py-4 px-4 font-mono text-white">
                            ${trade.investment?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`font-mono ${isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
                              {isPositive ? '+' : ''}${income.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`font-mono ${isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
                              {isPositive ? '+' : ''}{pnl.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(trade.status, "trade")}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawals Table */}
        {activeTab === "withdrawals" && (
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-[#1F1F1F]">
              {filteredWithdrawals.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">Нет заявок на вывод</div>
              ) : (
                filteredWithdrawals.map((w) => (
                  <div key={w.id} className="p-4" data-testid={`history-withdrawal-card-${w.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-white font-semibold">${w.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-[#71717A] text-xs">{w.asset}</span></span>
                      {getStatusBadge(w.status, "withdrawal")}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#A1A1AA] font-mono truncate max-w-[60%]">{w.wallet_address}</span>
                      <span className="text-[#71717A] font-mono">{formatShortDate(w.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1F1F1F]">
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сумма</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Актив</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Кошелёк</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWithdrawals.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-[#A1A1AA]">
                        Нет заявок на вывод
                      </td>
                    </tr>
                  ) : (
                    filteredWithdrawals.map((w) => (
                      <tr key={w.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50 transition-colors">
                        <td className="py-4 px-4 text-[#A1A1AA] text-sm font-mono">
                          {formatShortDate(w.created_at)}
                        </td>
                        <td className="py-4 px-4 font-mono text-white">
                          ${w.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-4 px-4 text-white">
                          {w.asset}
                        </td>
                        <td className="py-4 px-4 text-[#A1A1AA] text-sm max-w-[200px] truncate">
                          {w.wallet_address}
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(w.status, "withdrawal")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Deposits Table */}
        {activeTab === "deposits" && (
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-[#1F1F1F]">
              {filteredDeposits.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">Нет пополнений</div>
              ) : (
                filteredDeposits.map((d) => (
                  <div key={d.id} className="p-4" data-testid={`history-deposit-card-${d.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[#34D399] font-semibold">+${d.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-[#71717A] text-xs">{d.asset}</span></span>
                      <span className="inline-flex items-center gap-1 bg-[#34D399]/15 text-[#34D399] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                        <CheckCircle size={12} /> Зачислено
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#A1A1AA]">{d.network}</span>
                      <span className="text-[#71717A] font-mono">{formatShortDate(d.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1F1F1F]">
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сумма</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Актив</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сеть</th>
                    <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeposits.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-[#A1A1AA]">
                        Нет пополнений
                      </td>
                    </tr>
                  ) : (
                    filteredDeposits.map((d) => (
                      <tr key={d.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50 transition-colors">
                        <td className="py-4 px-4 text-[#A1A1AA] text-sm font-mono">
                          {formatShortDate(d.created_at)}
                        </td>
                        <td className="py-4 px-4 font-mono text-[#34D399]">
                          +${d.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-4 px-4 text-white">
                          {d.asset}
                        </td>
                        <td className="py-4 px-4 text-white">
                          {d.network}
                        </td>
                        <td className="py-4 px-4">
                          <span className="inline-flex items-center gap-1 bg-[#34D399]/15 text-[#34D399] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                            <CheckCircle size={12} />
                            Зачислено
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default History;
